import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { resetVoiceSession, voiceSessionIdSelector } from './voiceSessionSlice';

import type { RootState } from 'features/app/store/store';

export interface VoiceSessionHandoffState {
  handedOffSessionId: string | null;
}

export const initialVoiceSessionHandoffState: VoiceSessionHandoffState = {
  handedOffSessionId: null,
};

/**
 * Records which shared backend voice session now belongs to someone else
 * (next interpreter, AST SignMail agent, teammate), so this client may only
 * tear down its local session and must not delete the backend one.
 *
 * The handoff is keyed by session id, so a leftover entry can never suppress
 * the delete of a later voice session.
 *
 * It lives outside `voiceSessionSlice` because `setVoiceSession` replaces that
 * whole state and would drop the handoff mid-flow.
 */
export const voiceSessionHandoffSlice = createSlice({
  name: 'voiceSessionHandoff',
  initialState: initialVoiceSessionHandoffState,
  reducers: {
    voiceSessionHandedOff: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      if (action.payload.sessionId) {
        state.handedOffSessionId = action.payload.sessionId;
      }
    },
    voiceSessionHandoffFailed: () => initialVoiceSessionHandoffState,
  },
  extraReducers: (builder) => {
    builder.addCase(resetVoiceSession, () => initialVoiceSessionHandoffState);
  },
});

export const { voiceSessionHandedOff, voiceSessionHandoffFailed } =
  voiceSessionHandoffSlice.actions;

export const voiceSessionHandoffReducer = voiceSessionHandoffSlice.reducer;

export const handedOffVoiceSessionIdSelector = ({
  voiceSessionHandoff,
}: RootState) => voiceSessionHandoff.handedOffSessionId;

export const isCurrentVoiceSessionHandedOffSelector = (state: RootState) => {
  const currentSessionId = voiceSessionIdSelector(state);

  return (
    Boolean(currentSessionId) &&
    handedOffVoiceSessionIdSelector(state) === currentSessionId
  );
};
